import { useState } from 'react';
import { Check, ChevronsUpDown, UploadCloud, FileText, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

export const inputCls = 'w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] focus:border-[var(--jf-primary)] focus:ring-4 focus:ring-[#E05A47]/15';

export const FormCard = ({ step, title, icon: Icon, children, testId }) => (
  <section data-testid={testId} className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-5 sm:p-8">
    <div className="flex items-center gap-3 mb-6">
      <span className="w-10 h-10 rounded-xl bg-orange-100 text-[var(--jf-primary)] flex items-center justify-center font-black">
        {Icon ? <Icon className="w-5 h-5" /> : step}
      </span>
      <h2 className="text-xl sm:text-2xl font-extrabold text-[var(--jf-navy)]">{title}</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </section>
);

export const Field = ({ label, required, error, hint, full, children }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label} {required && <span className="text-[var(--jf-primary)]">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    {error && <p className="text-xs text-red-600 mt-1 font-medium">{error}</p>}
  </div>
);

export const Chips = ({ options, value, onChange, multi = true, testId }) => {
  const sel = multi ? value || [] : value;
  const toggle = (o) => {
    if (!multi) return onChange(o);
    onChange(sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => {
        const on = multi ? sel.includes(o) : sel === o;
        return (
          <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${testId}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors duration-150 ${on ? 'bg-[var(--jf-primary)] border-[var(--jf-primary)] text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-[var(--jf-primary)]'}`}>
            {on && multi && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}{o}
          </button>
        );
      })}
    </div>
  );
};

export const SearchSelect = ({ options, value, onChange, placeholder = 'Select', disabled, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testId}
          className={`${inputCls} flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed`}>
          <span className={`truncate ${value ? '' : 'text-slate-400'}`}>{value || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[16rem]" align="start">
        <Command>
          <CommandInput placeholder="Type to search..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => { onChange(o); setOpen(false); }} data-testid={`${testId}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <Check className={`w-4 h-4 mr-2 ${value === o ? 'opacity-100 text-[var(--jf-primary)]' : 'opacity-0'}`} />{o}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const FileField = ({ accept, maxMb, file, onChange, testId, label = 'Choose file' }) => (
  <div>
    {file ? (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 h-12">
        <FileText className="w-5 h-5 text-emerald-700 shrink-0" />
        <span className="text-sm font-medium text-emerald-900 truncate flex-1" data-testid={`${testId}-name`}>{file.name}</span>
        <button type="button" onClick={() => onChange(null)} data-testid={`${testId}-remove`} className="text-emerald-800 hover:text-red-600"><X className="w-4 h-4" /></button>
      </div>
    ) : (
      <label className="flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-4 h-12 cursor-pointer transition-colors hover:border-[var(--jf-primary)] hover:bg-orange-50/50">
        <UploadCloud className="w-5 h-5 text-[var(--jf-primary)]" />
        <span className="text-sm text-slate-600">{label} <span className="text-slate-400">({accept.replaceAll(',', ', ')} · max {maxMb} MB)</span></span>
        <input type="file" className="hidden" accept={accept} data-testid={testId}
          onChange={(e) => { onChange(e.target.files?.[0] || null); e.target.value = ''; }} />
      </label>
    )}
  </div>
);

export const checkFile = (file, accept, maxMb) => {
  if (!file) return '';
  const ext = `.${file.name.split('.').pop().toLowerCase()}`;
  if (!accept.split(',').includes(ext)) return `Allowed: ${accept}`;
  if (file.size > maxMb * 1024 * 1024) return `File must be under ${maxMb} MB`;
  return '';
};
